import React, { useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import SignalIcon from "./SignalIcon";
import SignalPicker from "./SignalPicker";
import SignalDescription, { Badges } from "./SignalDescription";
import { FAMILIES, familyOf, costText, costUnitOf, isSignal, isAlwaysOn, signalMatches, tierOf, badgesOf } from "./signalMeta";
import { FALLBACK_TEMPLATES } from "./custom/customSignalMeta";
import "./custom/customSignals.scss";

const COST = [
  { value: "any", label: "Any cost" },
  { value: "free", label: "Free" },
  { value: "paid", label: "Uses credits" },
  { value: "deep", label: "Deep intel" }
];
const costPasses = (cost, def) =>
  cost === "any" || (cost === "deep" ? tierOf(def) === "deep" : (cost === "free") === (costUnitOf(def) === "free"));
const STATUS = [
  { value: "any", label: "Any status" },
  { value: "tracking", label: "Tracking" },
  { value: "paused", label: "Paused" },
  { value: "none", label: "Not tracked" }
];
/** Signal ids from most to least valuable to a rep; anything not listed sits after these. */
const VALUE = ["job_change", "champion_move", "new_leader", "hiring_intel", "positive_reply", "came_back", "hot_opens", "went_quiet",
  "headcount_growth", "link_click", "connection_accepted", "headcount_decline", "left_company"];
const labelOf = (list, v) => (list.find(o => o.value === v) || {}).label;
export const firstSentence = text => {
  const m = String(text || "").match(/^.*?\.(?=\s|$)/);
  return m ? m[0] : String(text || "");
};

/** Where the member stands with a signal type: tracking (any watch running), paused (all paused) or none. */
const statusOf = def => {
  const rows = signalsData.saved.filter(s => s.signal && s.signal.signalType === def.id);
  const running = rows.find(r => r.signal.status !== "PAUSED");
  if (running) return { status: "tracking", row: running };
  if (rows.length) return { status: "paused", row: rows[0] };
  if (isAlwaysOn(def)) return { status: "on", row: null };
  return { status: "none", row: null };
};

const SignalCard = observer(({ def, onTrack, onHowItWorks, onOpenTracker, recommended = false }) => {
  const { status, row } = statusOf(def);
  const resume = async () => {
    if (await signalsData.setPaused(row.signal.id, false)) toasterState.setToastMsg("Tracking resumed", "success");
  };
  return (
    <div className="sg-card">
      <div className="sg-card__head">
        <span className={`sg-tile ${familyOf(def.category).key}`}><SignalIcon id={def.id} size={20} /></span>
        <span className="sg-card__title">{def.label}</span>
        {status === "tracking" && <span className="ag-pill ag-pill--live"><i className="ag-dot" />Tracking</span>}
        {status === "paused" && <span className="ag-pill">Paused</span>}
        {status === "on" && <span className="ag-pill ag-pill--live"><i className="ag-dot" /> On</span>}
        {status === "none" && def.available === false && <span className="sg-pill sg-pill--soon">Coming soon</span>}
        {recommended && <span className="sg-pill sg-pill--rec">Recommended</span>}
      </div>
      <Badges def={def} />
      <SignalDescription def={def} fallback={<p>{firstSentence(def.description)}</p>} />
      {/* A free card already says so in its badge. */}
      {!badgesOf(def).some(b => b.key === "free") && <div className="sg-card__cost"><SignalIcon id="coin" size={14} />{costText(def)}</div>}
      <div className="sg-card__foot">
        {status === "tracking" && <button className="sg-btn sg-btn--small sg-btn--outline" onClick={() => onOpenTracker(row.signal.id)}>Open feed</button>}
        {status === "paused" && <button className="sg-btn sg-btn--small sg-btn--outline" onClick={resume}>Resume</button>}
        {status === "none" && def.available !== false && <button className={`sg-btn sg-btn--small ${recommended ? "" : "sg-btn--outline"}`} onClick={() => onTrack(def)}><SignalIcon id="plus" size={14} /> Track</button>}
        <button className="sg-how" onClick={() => onHowItWorks(def)}><SignalIcon id="info" size={14} /> How it works</button>
      </div>
    </div>
  );
});

// Not in the catalog: the competitors card carries its own three-part description, in the catalog's shape.
const COMPETITORS_DEF = {
  id: "competitors",
  tier: "free",
  creditsPerUnit: 0,
  details: {
    reads: "Their company page and what their people post, from the competitors in your seller profile.",
    gives: "The teams they're building, what they ship, where they're headed, and the customers, leaders and markets they're going after.",
    example: "Products: LeadIQ launched a Power Dialer. What it means for you: lead with your verified data."
  }
};
const COMPETITORS_LINE = Object.values(COMPETITORS_DEF.details).join(" ");

/** Not a tracked signal: a standing view of the competitors in the seller profile. Always on, no credits. */
const CompetitorsCard = () => {
  const history = useHistory();
  return (
    <div className="sg-card">
      <div className="sg-card__head">
        <span className="sg-tile cmp"><SignalIcon id="competitors" size={20} /></span>
        <span className="sg-card__title">Your competitors</span>
        <span className="ag-pill ag-pill--live"><i className="ag-dot" /> On</span>
      </div>
      <Badges def={COMPETITORS_DEF} />
      <SignalDescription def={COMPETITORS_DEF} />
      <div className="sg-card__cost">Uses your seller profile</div>
      <div className="sg-card__foot">
        <button className="sg-btn sg-btn--small sg-btn--outline" onClick={() => history.push("/signals/competitors")}>See competitors</button>
      </div>
    </div>
  );
};

const DEEP = { tier: "deep", costUnit: "company", creditsPerUnit: 2 };
const CREATE_LINE = "create your own describe custom tell me when topic posts talks about";

/** Last in the grid, after every ready-made signal: describe one in your own words. */
const CreateCustomCard = ({ onCreate }) => (
  <div className="sg-card cs-card">
    <div className="sg-card__head">
      <span className="sg-tile cus"><SignalIcon id="sparkle" size={20} /></span>
      <span className="sg-card__title">Create your own</span>
    </div>
    <Badges def={DEEP} />
    <div className="sg-desc">
      <div className="sg-desc__row"><b>You say</b><span>What to watch for, in your words. “Tell me when an account talks about moving off Salesforce.”</span></div>
      <div className="sg-desc__row"><b>You get</b><span>Each match in your feed, with the quote, who to reach and a draft.</span></div>
    </div>
    <div className="sg-card__cost"><SignalIcon id="coin" size={14} />2 credits per company, 1 per contact, each check. Preview free.</div>
    <div className="sg-card__foot">
      <button className="sg-btn sg-btn--small sg-btn--outline" onClick={() => onCreate(null)}><SignalIcon id="plus" size={14} /> Describe your own</button>
    </div>
  </div>
);

/** A starter: the phrase with its blank, and an example. */
const TemplateCard = ({ template, onCreate }) => (
  <div className="sg-card">
    <div className="sg-card__head">
      <span className="sg-tile cus"><SignalIcon id="sparkle" size={20} /></span>
      <span className="sg-card__title">{template.title}</span>
    </div>
    <Badges def={DEEP} />
    <p className="cs-tpl__phrase">{template.phrase}.</p>
    {template.example && <div className="sg-desc__ex">Example: {template.example.replace(/^Tell me when /, "")}</div>}
    <div className="sg-card__cost"><SignalIcon id="coin" size={14} />2 credits per company, 1 per contact, each check. Preview free.</div>
    <div className="sg-card__foot">
      <button className="sg-btn sg-btn--small sg-btn--outline" onClick={() => onCreate(template)}><SignalIcon id="plus" size={14} /> Track</button>
    </div>
  </div>
);

/** Library: every signal you can track, filterable by family, cost and status. */
const SignalsLibrary = ({ onTrack, onHowItWorks, onOpenTracker, onCreateCustom }) => {
  const [query, setQuery] = useState("");
  const [fam, setFam] = useState("all");
  const [cost, setCost] = useState("any");
  const [status, setStatus] = useState("any");

  const all = signalsData.catalog.filter(isSignal);
  const famKey = d => familyOf(d.category).key;
  const q = query.trim().toLowerCase();
  const passes = d =>
    signalMatches(d, q) &&
    costPasses(cost, d) &&
    (status === "any" || statusOf(d).status === status || (status === "tracking" && statusOf(d).status === "on"));
  const matching = all.filter(passes);
  // Most valuable first: a person moving or a new buyer beats a page view. Coming soon goes last.
  const rankOf = d => (d.available === false ? 100 : VALUE.indexOf(d.id) < 0 ? 50 : VALUE.indexOf(d.id));
  const shown = (fam === "all" ? matching : matching.filter(d => famKey(d) === fam)).slice().sort((a, b) => rankOf(a) - rankOf(b));
  // The competitors card counts as one more entry in the competitor family; it has no cost and is always on.
  const compMatches = (!q || `your competitors ${COMPETITORS_LINE}`.toLowerCase().includes(q)) && (cost === "any" || cost === "free") && (status === "any" || status === "tracking");
  const compShown = compMatches && (fam === "all" || fam === "cmp");
  // Custom starters are Deep intel, never tracked as such: they show unless a filter rules them out.
  const templates = signalsData.customTemplates.length ? signalsData.customTemplates : FALLBACK_TEMPLATES;
  const customPasses = !!onCreateCustom && (cost === "any" || cost === "deep" || cost === "paid") && (status === "any" || status === "none");
  const tplMatch = customPasses ? templates.filter(t => !q || `${t.title} ${t.phrase} ${t.example || ""}`.toLowerCase().includes(q)) : [];
  const createMatch = customPasses && (!q || CREATE_LINE.includes(q));
  const customCount = tplMatch.length + (createMatch ? 1 : 0);
  const customFam = fam === "all" || fam === "cus";
  const tplShown = customFam ? tplMatch : [];
  const createShown = customFam && createMatch;
  const famCount = key => matching.filter(d => famKey(d) === key).length + (key === "cmp" && compMatches ? 1 : 0) + (key === "cus" ? customCount : 0);
  // One filled Track on the page: job changes, the signal most teams start with, while it is not tracked yet.
  const recommendedId = (shown.find(d => d.id === "job_change" && d.available !== false && statusOf(d).status === "none") || {}).id;
  const families = FAMILIES.filter(f => f.key === "cmp" || (f.key === "cus" && !!onCreateCustom) || all.some(d => famKey(d) === f.key));

  const tags = [
    fam !== "all" && { key: "fam", label: (FAMILIES.find(f => f.key === fam) || {}).label, clear: () => setFam("all") },
    q && { key: "q", label: `“${query.trim()}”`, clear: () => setQuery("") },
    cost !== "any" && { key: "cost", label: labelOf(COST, cost), clear: () => setCost("any") },
    status !== "any" && { key: "status", label: labelOf(STATUS, status), clear: () => setStatus("any") }
  ].filter(Boolean);
  const clearAll = () => { setFam("all"); setQuery(""); setCost("any"); setStatus("any"); };

  return (
    <div className="sg-lib">
      <div className="sg-fchips">
        <button className={`sg-chip ${fam === "all" ? "on" : ""}`} onClick={() => setFam("all")}>All · {matching.length + (compMatches ? 1 : 0) + customCount}</button>
        {families.map(f => (
          <button key={f.key} className={`sg-chip ${fam === f.key ? "on" : ""}`} onClick={() => setFam(f.key)}>
            {f.label} · {famCount(f.key)}
          </button>
        ))}
      </div>

      <div className="sg-bar">
        <div className="sg-search">
          <SignalIcon id="search" />
          <input className="sg-input" placeholder="Search signals, e.g. job change, hiring" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <SignalPicker value={cost} options={COST} onChange={setCost} prefix="Cost: " />
        <SignalPicker value={status} options={STATUS} onChange={setStatus} prefix="Status: " />
      </div>

      <div className="sg-applied">
        <span className="sg-applied__n">Showing {shown.length + (compShown ? 1 : 0) + tplShown.length + (createShown ? 1 : 0)} of {all.length + 1 + templates.length + 1} signals</span>
        {tags.map(t => (
          <span key={t.key} className="sg-atag">{t.label}<button aria-label={`Remove ${t.label}`} onClick={t.clear}><SignalIcon id="x" size={12} /></button></span>
        ))}
        {tags.length > 0 && <button className="sg-link" onClick={clearAll}>Clear all</button>}
      </div>

      {onCreateCustom && (
        <div className="sg-lib__own">
          Don't see what you need? <button className="sg-link" onClick={() => onCreateCustom(null)}>Describe your own signal →</button>
        </div>
      )}

      <div className="sg-grid">
        {shown.map(d => <SignalCard key={d.id} def={d} onTrack={onTrack} onHowItWorks={onHowItWorks} onOpenTracker={onOpenTracker} recommended={d.id === recommendedId} />)}
        {compShown && <CompetitorsCard />}
        {tplShown.map(t => <TemplateCard key={t.key} template={t} onCreate={onCreateCustom} />)}
        {createShown && <CreateCustomCard onCreate={onCreateCustom} />}
      </div>

      {shown.length === 0 && !compShown && !createShown && tplShown.length === 0 && (
        <div className="sg-empty">
          <div className="sg-empty__art"><SignalIcon id="search" /></div>
          <b>No signals match</b>
          <p>Try a different word, or clear the filters to see everything you can track.</p>
          {tags.length > 0 && <button className="sg-btn sg-btn--ghost" onClick={clearAll}>Clear all</button>}
        </div>
      )}

      <div className="sg-credits-note">
        <SignalIcon id="coin" size={15} />
        <span>Tracking costs 1 credit per contact or company, each time it is checked (monthly by default, weekly if you choose). Deep intel costs 2 credits per company, each check. Everything it finds is included. Your own activity and your competitors are free.</span>
      </div>
    </div>
  );
};

export default observer(SignalsLibrary);
